import React, { ComponentType } from 'react';
import { ShareBar, TailwindContainer } from '../index';
import { ShowcaseImage, ShowcaseImageContentContainer } from './Showcase.style';
import { cn } from '../cn';
import { ClassValue } from 'clsx';

type ShowcaseProps = React.PropsWithChildren<{
  title: string,
  subtitle?: string,
  content?: string | JSX.Element,
  button?: JSX.Element,
  image: string,
  align?: string,
  small?: boolean,
  sharing?: boolean,
  className?: ClassValue[],
  imageContent?: JSX.Element,
  id?: string,
}>;

export const Showcase: React.FC<ShowcaseProps> = ({
  title, subtitle, content, button, image, align='left',
  small=false, sharing=false, imageContent, className, id,
}) => {
  return (
    <section id={id} className={cn(
      'relative',
      'flex',
      'flex-col',
      'pb-8',
      'print:block',
      'print:pb-0',
      'print:pt-8',
      'lg:py-40',
      'lg:px-0',
      'xl:py-64',
      'bg-black',
      {
        'lg:py-20': small,
        'xl:py-40': small,
      },
      ...(className || '')
    )}>
      <TailwindContainer
        className={[
          'w-full',
          'order-2',
          'relative',
          '-top-32',
          'flex-col',
          'z-10',
          {
            'items-start': align === 'left',
            'items-end': align !== 'left',
          },
          'print:top-0',
          'lg:top-0',
          {
            "after:content-['']": !button,
            'after:block': !button,
            'after:absolute': !button,
            'after:bottom-0': !button,
            'after:right-1/4': !button && align === 'left',
            'after:left-1/4': !button && align !== 'left',
            'after:w-[calc(75%-15px)]': !button,
            'after:h-px': !button,
            'after:bg-white': !button,
          },
        ]}
      >
        {subtitle && <div className={cn(
          'mb-4',
          'lg:mb-6',
          'italic',
          { 'text-left': align === 'left' },
          { 'text-right': align !== 'left' },
          'max-w-[600px]',
          'print:max-w-none',
          'print:text-black',
        )}>{subtitle}</div>}
        <h2 className={cn(
          { 'text-left': align === 'left' },
          { 'text-right': align !== 'left' },
          'max-w-96',
          'print:max-w-none',
          'print:text-black',
          'lg:text-6xl',
          'lg:max-w-[600px]',
        )}>
          {title}
        </h2>
        {content && <div className={cn(
          'mb-4',
          'lg:mb-6',
          { 'text-left': align === 'left' },
          { 'text-right': align !== 'left' },
          'max-w-[600px]',
          'print:max-w-none',
          'print:text-black',
        )}>
          {content}
        </div>}
        {button}
        {sharing && <ShareBar title={title} image={image} />}
      </TailwindContainer>
      <ShowcaseImageContentContainer image={image} align={align}>
        {imageContent ? imageContent : ''}
      </ShowcaseImageContentContainer>
      <div
        className={cn(
          'order-1',
          'h-[400px]',
          'w-full',
          'relative',
          'bg-cover',
          'bg-center',
          'z-0',
          'print:hidden',
          'lg:absolute',
          'lg:top-0', 'lg:bottom-0',
          { 'right-0': align === 'left' },
          { 'left-0': align !== 'left' },
          'lg:h-auto',
          'lg:w-1/2',
          "after:content-['']",
          'after:block',
          'after:absolute',
          'after:top-0', 'after:bottom-0', 'after:left-0', 'after:right-0',
          'after:bg-gradient-to-t',
          'after:from-black',
          'after:to-transparent',
          { 'lg:after:bg-gradient-to-r': align === 'left' },
          { 'lg:after:bg-gradient-to-l': align !== 'left' },
        )}
        style={{ backgroundImage: `url(${image})` }}
      />
    </section>
  );
};
